import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: 'infrastructure',
    warehouse_size: '',
    location: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        project_type: 'infrastructure',
        warehouse_size: '',
        location: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>Contact Arthaa Build | Get Free Precast Concrete Project Analysis — Chandigarh</title>
        <meta name="description" content="Contact Arthaa Build for precast concrete construction, brand leasing, or property tech inquiries. Free project analysis. Located in Chandigarh, serving Punjab, Haryana, Delhi NCR, UP, HP." />
        <link rel="canonical" href="https://arthaabuild.com/contact" />
      </Helmet>

      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-widest text-[#7EB3D8] font-semibold mb-4">Get In Touch</p>
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-8">
              Let's Build Together
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Ready to start your project? Our team is here to provide expert consultation and bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="-mt-20 pb-24" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1 space-y-6"
            >
              <div className="luxury-card bg-white p-8 rounded-lg" data-testid="contact-phone">
                <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mb-4">
                  <Phone className="w-6 h-6" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-slate-900 mb-2">Phone</h3>
                <a href="https://wa.me/917973598780" className="text-[#2B5278] hover:text-[#1E3A5F] font-medium transition-colors">
                  +91 79735 98780
                </a>
                <p className="text-sm text-slate-500 mt-1">WhatsApp Available</p>
              </div>

              <div className="luxury-card bg-white p-8 rounded-lg" data-testid="contact-email">
                <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mb-4">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-slate-900 mb-2">Email</h3>
                <a href="mailto:info@arthaabuild.com" className="text-[#2B5278] hover:text-[#1E3A5F] font-medium transition-colors">
                  info@arthaabuild.com
                </a>
              </div>

              <div className="luxury-card bg-white p-8 rounded-lg" data-testid="contact-address">
                <div className="w-14 h-14 bg-[#2B5278]/10 rounded-full flex items-center justify-center text-[#2B5278] mb-4">
                  <MapPin className="w-6 h-6" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-slate-900 mb-2">Office</h3>
                <p className="text-slate-600">
                  Industrial Area, Chandigarh<br />
                  Haryana, India - 132103
                </p>
              </div>

              <div className="luxury-card bg-gradient-to-br from-[#2B5278] to-[#1E3A5F] p-8 rounded-lg text-white" data-testid="contact-hours">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6" />
                </div>
                <h3 className="font-serif font-semibold text-xl mb-2">Business Hours</h3>
                <p className="text-white/90 text-sm">
                  Monday - Saturday<br />
                  9:00 AM - 6:00 PM IST
                </p>
              </div>
            </motion.div>

            {/* Inquiry Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="luxury-card bg-white p-10 lg:p-12 rounded-lg">
                <h2 className="font-serif font-bold text-3xl text-slate-900 mb-2">Project Inquiry</h2>
                <p className="text-slate-600 mb-8">Fill out the form below and we'll respond within 24 hours.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-phone"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-slate-900 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-company"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="project_type" className="block text-sm font-semibold text-slate-900 mb-2">
                        Project Type *
                      </label>
                      <select
                        id="project_type"
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        required
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-project-type"
                      >
                        <option value="infrastructure">Infrastructure</option>
                        <option value="commercial">Commercial</option>
                        <option value="residential">Residential</option>
                        <option value="industrial">Industrial</option>
                        <option value="hospital">Hospital</option>
                        <option value="hotel">Hotel</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="warehouse_size" className="block text-sm font-semibold text-slate-900 mb-2">
                        Project Size (sq ft)
                      </label>
                      <input
                        type="text"
                        id="warehouse_size"
                        name="warehouse_size"
                        value={formData.warehouse_size}
                        onChange={handleChange}
                        placeholder="e.g., 50000"
                        className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                        data-testid="form-size"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-slate-900 mb-2">
                      Project Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="City, State"
                      className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all"
                      data-testid="form-location"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-2">
                      Project Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us about your project requirements..."
                      className="w-full border border-slate-300 focus:border-[#2B5278] focus:ring-2 focus:ring-[#2B5278]/20 rounded-md px-4 py-3 text-slate-900 outline-none transition-all resize-none"
                      data-testid="form-message"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] hover:from-[#1E3A5F] hover:to-[#2B5278] disabled:from-slate-300 disabled:to-slate-300 text-white font-semibold text-base px-6 py-4 rounded-md transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    data-testid="form-submit"
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>
                  
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md" data-testid="form-success">
                      <p className="font-semibold mb-1">Thank you for your inquiry!</p>
                      <p className="text-sm">We'll respond within 24 hours.</p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md" data-testid="form-error">
                      <p className="font-semibold mb-1">Submission failed.</p>
                      <p className="text-sm">Please try again or contact us directly.</p>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>

          {/* Map */}
          <div className="mt-12 luxury-card bg-white p-2 rounded-lg overflow-hidden" data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55841.89288584648!2d76.9284!3d29.3909!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ddb9bb5c1d2f7%3A0x6e3f7c6c8e7a7c6e!2sChandigarh%2C%20Haryana!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '0.5rem' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Arthaa Build Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;